// deps
import React, { memo, useCallback, useState } from "react";

// styles
import { Container, Content, ImageContainer, Separator, ClickableText } from "./styles";

// internal-deps
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ElementObserver } from "@core-ui/components/ElementObserver";

// components
import {
    BillDetailsBannerHalfCard,
    BillDetailsBannerHalfCardProps,
} from "@core-ui/components/bill-details-banner-half-card";

// test-ids
import { BILL_DETAILS_BANNER_TEST_IDS } from "@swiggy-private/cart-test-ids";

// constants
import { DIMENSIONS } from "./constants";

export interface BillDetailsBannerProps {
    bannerType?: string;
    title?: string;
    subtitle?: string;
    icon?: string;
    titleColor?: string;
    subtitleColor?: string;
    bannerBgColor?: string;
    halfCardData?: BillDetailsBannerHalfCardProps;
    analytics?: {
        onImpressionBanner: (bannerType?: string) => void;
        onClickBanner: (bannerType?: string) => void;
        onImpressionBannerHalfcard: (bannerType?: string) => void;
        onClickCloseBannerHalfcard: (bannerType?: string) => void;
    };
}

const BillDetailsBanner = ({
    bannerType,
    title,
    subtitle,
    icon,
    titleColor,
    subtitleColor,
    bannerBgColor,
    halfCardData,
    analytics,
}: BillDetailsBannerProps) => {
    const { onImpressionBanner, onClickBanner, onImpressionBannerHalfcard, onClickCloseBannerHalfcard } =
        analytics ?? {};
    const [showHalfcard, setShowHalfcard] = useState(false);

    const handleViewEnter = useCallback(() => {
        onImpressionBanner?.(bannerType);
    }, [onImpressionBanner, bannerType]);

    const openHalfcard = useCallback(() => {
        setShowHalfcard(true);
        onClickBanner?.(bannerType);
        onImpressionBannerHalfcard?.(bannerType);
    }, [onClickBanner, onImpressionBannerHalfcard, bannerType]);

    const halfCardClickHandler = useCallback(() => {
        setShowHalfcard(false);
        onClickCloseBannerHalfcard?.(bannerType);
    }, [onClickCloseBannerHalfcard, bannerType]);

    const renderFormattedText = useCallback(
        (text?: string) => {
            if (!text) return null;

            // Split the subtitle by the pattern [[...]].
            // After splitting, parts at odd indices correspond to content that was inside [[...]]
            // and should be rendered as clickable text.
            const parts = text.split(/\[\[|\]\]/);

            // Render each segment: odd indices (previously inside [[...]]) as ClickableText,
            // and even indices as plain text fragments.
            return parts.map((part, idx) =>
                idx % 2 === 1 ? (
                    <ClickableText
                        key={idx}
                        onClick={openHalfcard}
                        data-testid={BILL_DETAILS_BANNER_TEST_IDS.CLICKABLE_TEXT}
                    >
                        {part}
                    </ClickableText>
                ) : (
                    <React.Fragment key={idx}>{part}</React.Fragment>
                ),
            );
        },
        [openHalfcard],
    );

    return (
        <ElementObserver onViewEnter={handleViewEnter}>
            <Container backgroundColor={bannerBgColor} data-testid={BILL_DETAILS_BANNER_TEST_IDS.CONTAINER}>
                <Separator />
                <Content>
                    <Text
                        variant="V2.body3.neutral.medium"
                        customColor={{ light: titleColor ?? "", dark: titleColor ?? "" }}
                        data-testid={BILL_DETAILS_BANNER_TEST_IDS.TITLE}
                    >
                        {title}
                    </Text>
                    <Text
                        variant="V2.body3.neutral.medium"
                        customColor={{ light: subtitleColor ?? "", dark: subtitleColor ?? "" }}
                        data-testid={BILL_DETAILS_BANNER_TEST_IDS.SUBTITLE}
                    >
                        {renderFormattedText(subtitle)}
                    </Text>
                </Content>
                <ImageContainer>
                    <Image
                        cloudinaryId={icon}
                        width={DIMENSIONS.BANNER_IMAGE.WIDTH}
                        height={DIMENSIONS.BANNER_IMAGE.HEIGHT}
                    />
                </ImageContainer>

                <BillDetailsBannerHalfCard
                    {...halfCardData}
                    isOpen={showHalfcard}
                    clickHandler={halfCardClickHandler}
                />
            </Container>
        </ElementObserver>
    );
};

export default memo(BillDetailsBanner);
